import Layout from "@/components/Layout";
import { AppTooltip } from "@/components/AppTooltip";
import { ProductImageThumb } from "@/components/catalog/ProductImageThumb";
import { ProductLedgerDialog } from "@/components/catalog/ProductLedgerDialog";
import { ProductPaymentDialog } from "@/components/catalog/ProductPaymentDialog";
import { GenerateGatePassDialog } from "@/components/sales/GenerateGatePassDialog";
import { PaymentReceiptDialog } from "@/components/sales/PaymentReceiptDialog";
import { RecordBalancePaymentDialog } from "@/components/sales/RecordBalancePaymentDialog";
import { RecordInstallmentPaymentDialog, type InstallmentPaymentTarget } from "@/components/sales/RecordInstallmentPaymentDialog";
import { Button } from "@/components/ui/button";
import {
  investorSettlementErrorMessage,
  recordInvestorSettlement,
} from "@/lib/catalog/investorSettlementStore";
import {
  productPaymentErrorMessage,
  recordProductPayment,
} from "@/lib/catalog/productPaymentsStore";
import { buildCategoryProductSchema } from "@/lib/catalog/buildCategorySchema";
import { syncLinksForCategoryFromApi } from "@/lib/catalog/categoryAttributeLinksStore";
import { syncCategoriesFromApi } from "@/lib/catalog/categoriesStore";
import { syncCatalogAttributesFromApi, listCatalogAttributeRecords } from "@/lib/catalog/catalogAttributesStore";
import type { Attribute, AttributeValue } from "@/lib/catalog/types";
import {
  DEAL_TYPE_OPTIONS,
  PROFIT_RULE_OPTIONS,
} from "@/lib/catalog/dealPartners";
import { resolveProductThumbnailUrl } from "@/lib/catalog/productThumbnail";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";
import { formatCurrency, getActiveCompanyCurrency } from "@/lib/currencies/currenciesStore";
import { formatDeliveryPayload, nowDeliveryParts, parseDeliveryParts } from "@/lib/sales/gatePass";
import {
  fetchSaleInvoice,
  cancelSale,
  paySaleBalance,
  payBrokerCommission,
  paySaleInstallment,
  saleInvoiceErrorMessage,
  updateSaleDelivery,
  type ProfitDistributionSummary,
  type SaleInstallmentRecord,
  type SaleInvoiceRecord,
  type SalePaymentRecord,
  type SalePaymentStatus,
  type SaleProfitDistributionRecord,
} from "@/lib/sales/saleInvoiceStore";
import { installmentRemaining, installmentStatusClass, installmentStatusLabel } from "@/lib/sales/saleInstallments";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { format, isValid, parseISO } from "date-fns";
import {
  ArrowLeft,
  Ban,
  BookOpen,
  Building2,
  Calendar,
  Car,
  CheckCircle2,
  CreditCard,
  Download,
  // Edit, // TEMP: unused while Edit sale is hidden
  ExternalLink,
  FileText,
  HandCoins,
  Handshake,
  History,
  Loader2,
  Plus,
  Printer,
  Receipt,
  Search,
  ShieldCheck,
  Sparkles,
  Tag,
  Ticket,
  TrendingUp,
  User,
  UserCheck,
  Wallet,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/* ─── Formatting helpers ──────────────────────────────────────────── */

function formatAmount(n: number): string {
  return n.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function MoneyAmount({
  value,
  className,
  amountClassName,
  emphasis = false,
  showShort = false,
}: {
  value: number | null | undefined;
  className?: string;
  amountClassName?: string;
  emphasis?: boolean;
  showShort?: boolean;
}) {
  if (value == null || !Number.isFinite(Number(value))) {
    return <span className={cn("text-muted-foreground", className)}>—</span>;
  }

  const num = Number(value);
  const short = showShort ? pkrAmountShortLabel(num) : "";

  return (
    <span className={cn("inline-flex flex-col items-end tabular-nums", className)}>
      <span className={cn(emphasis ? "font-semibold text-foreground" : "font-medium text-foreground", amountClassName)}>
        {formatCurrency(num)}
      </span>
      {short ? <span className="text-[10px] text-muted-foreground">{short}</span> : null}
    </span>
  );
}

function paymentMethodLabel(method: string | null | undefined): string {
  if (method === "cash") return "Cash";
  if (method === "online") return "Online";
  if (method === "pay_order") return "Pay Order";
  if (method === "bank") return "Bank";
  if (method === "cheque") return "Cheque";
  return method ?? "—";
}

function tokenTypeLabel(type: string | null | undefined): string {
  if (type === "conditional") return "Conditional";
  if (type === "unconditional") return "Un-Conditional";
  return type ?? "—";
}

function vehicleStatusBadge(sale: SaleInvoiceRecord): { label: string; className: string } {
  if (sale.is_exchanged || sale.void_type === "exchange" || sale.payment_status === "exchanged") {
    return {
      label: "Exchanged",
      className: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
    };
  }
  if (sale.is_returned || sale.void_type === "return" || sale.payment_status === "returned") {
    return {
      label: "Returned",
      className: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
    };
  }
  if (sale.is_cancelled || sale.cancelled_at || sale.payment_status === "cancelled") {
    return {
      label: "Cancelled",
      className: "bg-slate-500/15 text-slate-700 dark:text-slate-300",
    };
  }

  const status = (sale.product?.inventory_status ?? "sold").toLowerCase();
  if (status === "in_stock") {
    return {
      label: "In Stock",
      className: "bg-sky-500/15 text-sky-700 dark:text-sky-400",
    };
  }
  if (status === "reserved") {
    return {
      label: "Reserved",
      className: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
    };
  }
  return {
    label: "Sold",
    className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  };
}

function PaymentStatusBadge({ status }: { status: SalePaymentStatus | null | undefined }) {
  if (!status) return null;
  const label =
    status === "paid"
      ? "Fully paid"
      : status === "partial"
        ? "Partially paid"
        : status === "exchanged"
          ? "Exchanged"
          : status === "returned"
          ? "Returned"
          : status === "cancelled"
            ? "Cancelled"
            : "Unpaid";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold",
        status === "paid"
          ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30"
          : status === "partial"
            ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30"
            : status === "returned"
              ? "bg-violet-500/15 text-violet-700 dark:text-violet-300 border border-violet-500/30"
              : status === "cancelled"
                ? "bg-slate-500/15 text-slate-700 dark:text-slate-300 border border-slate-500/30"
                : "bg-rose-500/15 text-rose-700 dark:text-rose-400 border border-rose-500/30",
      )}
    >
      {status === "paid" ? <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> : null}
      {label}
    </span>
  );
}

function optionLabel(options: { value: string; label: string }[], value: string | null | undefined): string {
  if (value == null || value === "") return "—";
  return options.find((o) => o.value === value)?.label ?? value;
}

function formatRatio(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return `${Number(n).toFixed(2)}%`;
}

/** Net-profit rules show profit share (`amount`); owner surplus/commission show payout amounts. */
function usesNetProfitShares(summary: ProfitDistributionSummary | null | undefined): boolean {
  const deal = summary?.deal_type;
  const rule = summary?.profit_rule_type;
  if (deal === "purchased") return true;
  if (deal === "partnership" && (rule === "commission" || rule === "profit_sharing")) return true;
  if (deal === "owner_vehicle" && rule === "profit_sharing") return true;
  return false;
}

function isOwnerSurplusSummary(summary: ProfitDistributionSummary | null | undefined): boolean {
  return summary?.deal_type === "owner_vehicle" && summary?.profit_rule_type === "owner_surplus";
}

function brokerCommissionPayerLabel(payer: string | null | undefined): string {
  if (payer === "seller") return "seller";
  if (payer === "overall") return "overall (net profit)";
  return "showroom";
}

function recipientShareAmount(
  row: {
    amount?: number | null;
    final_payable?: number | null;
  },
  summary: ProfitDistributionSummary | null | undefined,
): number | null {
  const profit = row.amount != null ? Number(row.amount) : null;
  const payable = row.final_payable != null ? Number(row.final_payable) : null;

  if (isOwnerSurplusSummary(summary)) {
    if (payable != null && Number.isFinite(payable)) return payable;
  } else if (usesNetProfitShares(summary)) {
    if (profit != null && Number.isFinite(profit)) return profit;
  } else {
    if (profit != null && Number.isFinite(profit) && profit > 0) return profit;
    if (payable != null && Number.isFinite(payable)) return payable;
  }

  if (payable != null && Number.isFinite(payable)) return payable;
  return profit;
}

function recipientShareBeforeBroker(
  row: {
    amount?: number | null;
    final_payable?: number | null;
    amount_before_broker?: number | null;
    final_payable_before_broker?: number | null;
    broker_commission_deducted?: number | null;
  },
  summary: ProfitDistributionSummary | null | undefined,
): number | null {
  const deducted = Number(row.broker_commission_deducted ?? 0);
  if (deducted <= 0.009) return null;

  if (isOwnerSurplusSummary(summary)) {
    const before = row.final_payable_before_broker != null ? Number(row.final_payable_before_broker) : null;
    if (before != null && Number.isFinite(before)) return before;
  }

  const beforeAmount = row.amount_before_broker != null ? Number(row.amount_before_broker) : null;
  if (beforeAmount != null && Number.isFinite(beforeAmount)) return beforeAmount;

  const net = recipientShareAmount(row, summary);
  if (net == null || !Number.isFinite(net)) return null;
  return Math.round((net + deducted) * 100) / 100;
}

function BrokerCommissionCalc({
  before,
  deducted,
  after,
  currSymbol,
}: {
  before: number;
  deducted: number;
  after: number;
  currSymbol: string;
  label?: string;
}) {
  return (
    <div className="mt-2 space-y-1 border-t border-border/30 pt-2 text-[11px] tabular-nums">
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground">Share before</span>
        <span className="font-medium text-foreground">
          {currSymbol} {formatAmount(before)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground">Broker commission</span>
        <span className="font-medium text-foreground">
          − {currSymbol} {formatAmount(deducted)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-foreground">Net share</span>
        <span className="font-semibold text-foreground">
          {currSymbol} {formatAmount(after)}
        </span>
      </div>
    </div>
  );
}

/** Generate synthetic receipt ID from invoice number and payment index. */
function makeReceiptId(invoiceNo: string, index: number): string {
  const suffix = String.fromCharCode(65 + index); // A, B, C, ...
  return `REC-${invoiceNo}-${suffix}`;
}

/** Map payment method to a styled badge config. */
function paymentMethodBadge(method: string | null | undefined): { label: string; emoji: string; className: string } {
  if (method === "cash") return { label: "Cash", emoji: "💵", className: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/60" };
  if (method === "online") return { label: "Online IBFT", emoji: "🌐", className: "bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300 border-sky-200 dark:border-sky-800/60" };
  if (method === "cheque") return { label: "Cheque", emoji: "🏛", className: "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 border-amber-200 dark:border-amber-800/60" };
  if (method === "pay_order") return { label: "Pay Order", emoji: "🏦", className: "bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800/60" };
  if (method === "bank") return { label: "Bank", emoji: "🏦", className: "bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300 border-sky-200 dark:border-sky-800/60" };
  return { label: method ?? "—", emoji: "💳", className: "bg-muted/50 text-muted-foreground border-border/50" };
}

/* ─── Sub-components ──────────────────────────────────────────────── */

function SummaryRow({
  label,
  value,
  emphasis = false,
  muted = false,
  borderTop = false,
}: {
  label: string;
  value: ReactNode;
  emphasis?: boolean;
  muted?: boolean;
  borderTop?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex min-w-0 items-start justify-between gap-2 py-1.5 text-sm",
        borderTop && "mt-0.5 border-t border-border/40 pt-2",
      )}
    >
      <span
        className={cn(
          "min-w-0 flex-1 break-words leading-snug text-muted-foreground",
          emphasis && "font-medium text-foreground",
        )}
      >
        {label}
      </span>
      <span className={cn("max-w-[58%] shrink-0 text-right leading-snug", muted && "text-muted-foreground")}>
        {value}
      </span>
    </div>
  );
}

function MetaBadge({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md border border-border/50 bg-background px-2 py-0.5 text-[11px] font-medium text-foreground">
      {children}
    </span>
  );
}

function SectionCard({
  title,
  subtitle,
  badge,
  icon: Icon,
  children,
  action,
  className,
  bodyClassName,
}: {
  title: string;
  subtitle?: ReactNode;
  badge?: ReactNode;
  icon?: typeof Wallet;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn("glass-card h-full flex flex-col overflow-hidden", className)}>
      <div className="flex flex-col gap-2.5 border-b border-border/30 bg-muted/15 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:px-4 lg:px-5 lg:py-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {Icon ? (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 text-primary">
              <Icon className="h-4 w-4" />
            </div>
          ) : null}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="whitespace-nowrap text-sm font-bold text-foreground lg:text-base">{title}</h2>
              {badge}
            </div>
            {subtitle ? (
              <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
            ) : null}
          </div>
        </div>
        {action ? <div className="min-w-0 w-full sm:w-auto shrink-0">{action}</div> : null}
      </div>
      <div className={cn("min-w-0 p-3 sm:p-4 lg:p-5 flex-1 flex flex-col justify-between", bodyClassName)}>{children}</div>
    </section>
  );
}

function RecipientCard({
  name,
  percent,
  amount,
  note,
  emphasis = false,
  payable,
  paid,
  balance,
  index,
}: {
  name: string;
  percent?: number | null;
  amount?: number | null;
  note?: string | null;
  emphasis?: boolean;
  payable?: number | null;
  paid?: number | null;
  balance?: number | null;
  index?: number;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border px-3 py-2.5",
        emphasis ? "border-primary/25 bg-primary/[0.05]" : "border-border/40 bg-background/70",
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          {index != null ? (
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary mt-0.5">
              {index}
            </span>
          ) : null}
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{name}</p>
            {note ? <p className="mt-0.5 text-[11px] text-muted-foreground">{note}</p> : null}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[11px] font-medium tabular-nums text-muted-foreground">{formatRatio(percent)}</p>
          <MoneyAmount value={amount} emphasis />
        </div>
      </div>

      {payable != null && (payable > 0 || (paid ?? 0) > 0 || (balance ?? 0) > 0) ? (
        <div className="mt-2.5 grid grid-cols-3 gap-2 border-t border-border/25 pt-2 text-[10px] text-muted-foreground leading-normal">
          <div>
            <span className="block text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80">Payable</span>
            <MoneyAmount value={payable} />
          </div>
          <div>
            <span className="block text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80">Paid</span>
            <MoneyAmount value={paid} amountClassName="text-primary" />
          </div>
          <div>
            <span className="block text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80">Balance</span>
            <MoneyAmount value={balance} emphasis />
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ─── Main Component ──────────────────────────────────────────────── */

const SaleInvoice = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [sale, setSale] = useState<SaleInvoiceRecord | null>(null);
  const [paymentTarget, setPaymentTarget] = useState<InstallmentPaymentTarget | null>(null);
  const [balanceDialogOpen, setBalanceDialogOpen] = useState(false);
  const [downPaymentDialogOpen, setDownPaymentDialogOpen] = useState(false);
  const [brokerPaymentDialogOpen, setBrokerPaymentDialogOpen] = useState(false);
  const [investorPayOpen, setInvestorPayOpen] = useState(false);
  const [investorPayOutstanding, setInvestorPayOutstanding] = useState(0);
  const [investorPayPartyId, setInvestorPayPartyId] = useState(0);
  const [investorPayPartyName, setInvestorPayPartyName] = useState("");
  const [investorPayProfitDistributionId, setInvestorPayProfitDistributionId] = useState<number | null>(null);
  const [gatePassOpen, setGatePassOpen] = useState(false);
  const [gatePassSaving, setGatePassSaving] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelSaving, setCancelSaving] = useState(false);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [selectedReceiptPayment, setSelectedReceiptPayment] = useState<SalePaymentRecord | null>(null);
  const [selectedReceiptId, setSelectedReceiptId] = useState("");
  const [extraProfitDetailsOpen, setExtraProfitDetailsOpen] = useState(false);
  const [gatePassDate, setGatePassDate] = useState(() => nowDeliveryParts().date);
  const [gatePassTime, setGatePassTime] = useState(() => nowDeliveryParts().time);
  const [checklistValues, setChecklistValues] = useState<Record<string, AttributeValue>>({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const [ledgerModalOpen, setLedgerModalOpen] = useState(false);

  // Payment ledger filters
  const [ledgerSearch, setLedgerSearch] = useState("");
  const [ledgerMethodFilter, setLedgerMethodFilter] = useState<string>("all");

  const canRecordPayment = can(getStoredAuthUser(), "sale_can_update") || can(getStoredAuthUser(), "sale_can_create");
  const canGenerateGatePass = canRecordPayment;
  const canCancelSale = canRecordPayment;
  // const canEditSale = can(getStoredAuthUser(), "sale_can_update") || can(getStoredAuthUser(), "sale_can_create");

  const checklistAttributes = useMemo(() => {
    const all = listCatalogAttributeRecords();
    return all.filter((a) => a.isDeliveryChecklist);
  }, [dataLoaded]);

  const reload = () => {
    if (!id) return Promise.resolve();
    return fetchSaleInvoice(id).then((r) => setSale(r));
  };

  useEffect(() => {
    if (!id) {
      setSale(null);
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(true);

    // Fetch essential catalog metadata for checklist resolution
    Promise.all([
      syncCategoriesFromApi(),
      syncCatalogAttributesFromApi(),
    ])
      .then(async () => {
        const r = await fetchSaleInvoice(id);
        if (mounted && r) {
          setSale(r);
          if (r.product?.category_id) {
            await syncLinksForCategoryFromApi(r.product.category_id);
          }
          setDataLoaded(true);
        }
      })
      .catch((e) => {
        toast.error(saleInvoiceErrorMessage(e));
        if (mounted) setSale(null);
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [id]);

  const invoiceNo = useMemo(() => (sale ? sale.invoice_no || String(sale.id) : ""), [sale?.invoice_no, sale?.id]);

  const installmentOutstanding = useMemo(() => {
    if (sale?.installment_outstanding != null) return sale.installment_outstanding;
    if (!sale?.installments?.length) return null;
    return sale.installments.reduce((sum, row) => sum + installmentRemaining(row), 0);
  }, [sale?.installment_outstanding, sale?.installments]);

  const installmentStats = useMemo(() => {
    const rows = sale?.installments ?? [];
    if (!rows.length) return null;
    const paidCount = rows.filter((r) => r.status === "paid").length;
    return { total: rows.length, paidCount };
  }, [sale?.installments]);

  const distributions = sale?.profit_distributions ?? [];
  const summary = sale?.profit_distribution_summary ?? null;
  const summaryPartners = summary?.partners ?? [];
  const netProfitValue = Number(summary?.net_profit ?? 0);
  const distributionTotalValue = Number(summary?.distribution_total ?? netProfitValue);
  const overallBrokerAmount =
    summary?.broker_commission_payer === "overall" ? Number(summary?.broker_commission ?? 0) : 0;
  const showOverallBrokerInDealTotals = overallBrokerAmount > 0.009;
  const grossProfitValue = showOverallBrokerInDealTotals
    ? Number(
        summary?.gross_profit ??
          netProfitValue + overallBrokerAmount,
      )
    : netProfitValue;
  // Seller/showroom: profit shares are net of broker — add broker back for Deal totals final line.
  // Overall: net_profit / distribution_total already reflect post-broker profit — do not add back.
  const brokerChargedToShare =
    summary?.broker_commission_payer === "seller" || summary?.broker_commission_payer === "showroom"
      ? Number(summary?.broker_commission ?? 0)
      : 0;
  const dealTotalsFinalDistribution = distributionTotalValue + brokerChargedToShare;
  const hasSummaryRecipients =
    Boolean(summary?.showroom) ||
    summaryPartners.length > 0 ||
    distributions.length > 0;

  const balanceAmount = sale?.balance_amount ?? sale?.total_amount ?? 0;
  const initialPaymentIsFull =
    Number(sale?.total_amount ?? 0) > 0 &&
    Number(sale?.token_amount ?? 0) >= Number(sale?.total_amount ?? 0) - 0.009;
  const initialPaymentReference =
    sale?.transaction_reference_no || sale?.pay_order_no || null;
  const productImageUrl = useMemo(() => resolveProductThumbnailUrl(sale?.product ?? null), [sale?.product]);

  // Filtered payments for the ledger
  const filteredPayments = useMemo(() => {
    if (!sale?.payments) return [];
    let filtered = sale.payments;

    // Method filter
    if (ledgerMethodFilter !== "all") {
      filtered = filtered.filter((p) => p.payment_method === ledgerMethodFilter);
    }

    // Search filter
    if (ledgerSearch.trim()) {
      const q = ledgerSearch.toLowerCase().trim();
      filtered = filtered.filter((p) => {
        const haystack = [
          p.bank_name,
          p.transaction_reference_no,
          p.pay_order_no,
          p.notes,
          p.transaction_type_label ?? p.transaction_type,
          p.payment_method_label ?? paymentMethodLabel(p.payment_method),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    return filtered;
  }, [sale?.payments, ledgerMethodFilter, ledgerSearch]);

  // Unique payment methods for filter pills
  const paymentMethods = useMemo(() => {
    if (!sale?.payments?.length) return [];
    const methods = new Set(sale.payments.map((p) => p.payment_method).filter(Boolean));
    return Array.from(methods) as string[];
  }, [sale?.payments]);

  if (!id) {
    return (
      <Layout title="Invoice">
        <div className="glass-card p-8 text-sm text-muted-foreground">Missing sale id.</div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout title="Invoice">
        <div className="glass-card p-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading invoice…
        </div>
      </Layout>
    );
  }

  if (!sale) {
    return (
      <Layout title="Invoice not found">
        <div className="space-y-4">
          <div className="glass-card p-8 text-sm text-muted-foreground">Invoice not found.</div>
          <Link to="/product-sales">
            <Button variant="glass" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to sales
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const customerMeta = [sale.customer?.phone ? `📞 ${sale.customer.phone}` : null, sale.customer?.city ? `City: ${sale.customer.city}` : null]
    .filter(Boolean)
    .join(" · ");

  const dealTypeLabel = optionLabel(DEAL_TYPE_OPTIONS, sale.product?.deal_type);
  const profitRuleLabel =
    sale.product?.deal_type === "purchased" && !sale.product?.profit_rule_type
      ? "Not applicable"
      : optionLabel(PROFIT_RULE_OPTIONS, sale.product?.profit_rule_type);

  const openGatePassModal = () => {
    const parts = parseDeliveryParts(sale?.delivery_date);
    setGatePassDate(parts.date);
    setGatePassTime(parts.time);

    // Populate checklist attributes initial values
    const initialValues: Record<string, AttributeValue> = {};
    if (sale?.product?.attributes) {
      for (const item of sale.product.attributes) {
        initialValues[item.code] = item.value;
      }
    }
    setChecklistValues(initialValues);

    setGatePassOpen(true);
  };

  const openInvestorPayDialog = (pd: SaleProfitDistributionRecord) => {
    const available = Number(pd.available ?? pd.balance ?? 0);
    const outstanding = available > 0.009 ? available : Number(pd.balance ?? 0);
    if (outstanding <= 0.009) return;
    setInvestorPayOutstanding(outstanding);
    setInvestorPayPartyId(Number(pd.party_id ?? 0));
    setInvestorPayPartyName(
      pd.party_name?.trim()
        || (sale?.product?.deal_type === "owner_vehicle" ? "Vehicle Owner / Investor" : "Deal Partner"),
    );
    setInvestorPayProfitDistributionId(Number(pd.id));
    setInvestorPayOpen(true);
  };

  const handleInvestorPayment = async (payload: {
    amount: number;
    payment_date: string;
    payment_method: string | null;
    funding_source?: "cash_bank" | "investor_balance";
    bank_id?: number | null;
    bank_name?: string | null;
    transaction_reference_no: string | null;
    pay_order_no: string | null;
    cheque_no?: string | null;
    deposit_slip_no?: string | null;
    notes: string | null;
    action: "pay" | "receive";
    party_id: number;
    transaction_type?: string | null;
    scheduled_payment_id?: number | null;
    product_invoice_id?: number | null;
    currency_code?: string | null;
    currency_amount?: number | null;
    exchange_rate?: number | null;
    settlement_mode?: "cash_pay" | "reinvest";
    profit_distribution_id?: number | null;
    destination_product_id?: number | null;
    destination_description?: string | null;
  }) => {
    const productId = sale?.product_id ?? sale?.product?.id;
    if (!productId) {
      toast.error("This sale has no linked product.");
      throw new Error("Missing product");
    }
    try {
      if (payload.settlement_mode === "reinvest") {
        if (!payload.profit_distribution_id) {
          throw new Error("Profit distribution is required for reinvestment.");
        }
        await recordInvestorSettlement(productId, {
          profit_distribution_id: Number(payload.profit_distribution_id),
          refund_amount: 0,
          reinvest_amount: payload.amount,
          destination_description: payload.destination_description?.trim() || null,
          destination_product_id: null,
          allocated_at: payload.payment_date,
          notes: payload.notes,
        });
        toast.success("Investor reinvestment recorded.");
      } else {
        await recordProductPayment(productId, {
          ...payload,
          bank_id: payload.bank_id ?? null,
          bank_name: payload.bank_name ?? null,
        });
        toast.success("Investor / owner payment recorded.");
      }
      await reload();
    } catch (e) {
      toast.error(investorSettlementErrorMessage(e) || productPaymentErrorMessage(e) || saleInvoiceErrorMessage(e));
      throw e;
    }
  };

  const submitGatePass = async () => {
    const payload = formatDeliveryPayload(gatePassDate, gatePassTime);
    if (!payload) {
      toast.error("Delivery date and time are required.");
      return;
    }
    setGatePassSaving(true);
    try {
      const r = await updateSaleDelivery(sale.id, payload, checklistValues);
      if (r.record) {
        setSale(r.record);
      } else {
        await reload();
      }
      toast.success(r.message);
      setGatePassOpen(false);
    } catch (e) {
      toast.error(saleInvoiceErrorMessage(e));
    } finally {
      setGatePassSaving(false);
    }
  };

  const submitCancelSale = async () => {
    if (!sale) return;
    setCancelSaving(true);
    try {
      const r = await cancelSale(sale.id, {
        notes: "Customer walk-away — sale cancelled",
        payment_method: sale.payment_method ?? "cash",
      });
      if (r.record) {
        setSale(r.record);
      } else {
        await reload();
      }
      toast.success(r.message);
      setCancelOpen(false);
    } catch (e) {
      toast.error(saleInvoiceErrorMessage(e));
    } finally {
      setCancelSaving(false);
    }
  };

  const isReturned = Boolean(sale.is_returned || sale.void_type === "return" || sale.payment_status === "returned");
  const isExchanged = Boolean(sale.is_exchanged || sale.void_type === "exchange" || sale.payment_status === "exchanged");
  const isCancelled = Boolean(
    sale.is_cancelled || sale.cancelled_at || sale.payment_status === "cancelled" || isReturned || isExchanged,
  );
  // TEMP: hide Edit sale until sale-edit / journal resync issues are settled.
  const canShowEdit = false;
  // const canShowEdit =
  //   canEditSale &&
  //   !isCancelled &&
  //   !isReturned &&
  //   !isExchanged &&
  //   !sale.is_installment;
  const isFullyPaid = sale.payment_status === "paid"
    || (
      Number(sale.outstanding ?? sale.balance_outstanding ?? 1) <= 0.009
      && Number(sale.paid_total ?? 0) > 0.009
    );
  const paidTotalAmount = Number(sale.paid_total ?? 0);
  const totalSaleAmount = Number(sale.total_amount ?? 0);
  const percentPaid = totalSaleAmount > 0 ? Math.min(100, Math.max(0, Math.round((paidTotalAmount / totalSaleAmount) * 100))) : 0;

  const currSymbol = getActiveCompanyCurrency().symbol;

  return (
    <Layout title={`Invoice ${invoiceNo}`}>
      <div className="w-full space-y-3.5">

        {/* ── 1. TOP BREADCRUMB & HEADER BAR ───────────────────────── */}
        <div className="space-y-2.5">
          <div className="flex items-center justify-between">
            <Link
              to="/product-sales"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors group"
            >
              <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
              Back to Sales
            </Link>
          </div>

          <div className="glass-card flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between lg:gap-3">
            <div className="min-w-0 space-y-1 lg:flex-1">
              <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/25 bg-primary/10 text-primary shadow-2xs">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="font-display text-xl font-bold text-foreground sm:text-2xl tracking-tight">
                      Invoice #{invoiceNo}
                    </h1>
                    <PaymentStatusBadge status={sale.payment_status} />
                    <span className="rounded-md border border-border/60 bg-muted/40 px-2.5 py-0.5 text-xs font-semibold text-foreground">
                      {sale.is_installment ? "Installment Sale" : sale.token_amount ? "Token Payment" : "Full Payment"}
                    </span>
                    {(() => {
                      const badge = vehicleStatusBadge(sale);
                      return (
                        <span className={cn("rounded-md px-2.5 py-0.5 text-xs font-semibold border", badge.className)}>
                          {badge.label}
                        </span>
                      );
                    })()}
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground/70" />
                    Created on {sale.date && isValid(parseISO(sale.date)) ? format(parseISO(sale.date), "MMMM do, yyyy") : (sale.date ?? "—")}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-nowrap items-center gap-2 shrink-0 overflow-x-auto">
              {(() => {
                // For installment sales, gate pass is eligible once down payment is fully received.
                // For non-installment sales, gate pass requires full payment ("paid" status).
                const gatePassEligible = sale.is_installment
                  ? (sale.down_payment_outstanding ?? 0) <= 0.009
                  : sale.payment_status === "paid";

                return (
                  <>
                    {canGenerateGatePass && !isCancelled && !gatePassEligible ? (
                      <AppTooltip
                        label={
                          sale.is_installment
                            ? `Gate Pass: Pending Down Payment (${formatCurrency(sale.down_payment_outstanding ?? 0)} remaining)`
                            : `Gate Pass: Pending Clearance (${formatCurrency(balanceAmount)} remaining)`
                        }
                      >
                        <span
                          className="inline-flex shrink-0 items-center justify-center h-9 w-9 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-300 shadow-2xs cursor-help"
                        >
                          <Ticket className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                        </span>
                      </AppTooltip>
                    ) : null}
                    {canGenerateGatePass && !isCancelled && gatePassEligible && !sale.delivery_date ? (
                      <AppTooltip label="Generate Gate Pass">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="shrink-0 h-9 w-9 p-0 border-border/70 bg-background text-foreground hover:bg-muted hover:text-foreground hover:border-border font-semibold shadow-2xs"
                          onClick={openGatePassModal}
                        >
                          <Ticket className="h-4 w-4 text-primary" />
                        </Button>
                      </AppTooltip>
                    ) : null}
                    {!isCancelled && gatePassEligible && sale.delivery_date ? (
                      <>
                        <AppTooltip label="View Gate Pass (PDF)">
                          <a href={`/sales/${sale.id}/gate-pass/pdf`} target="_blank" rel="noreferrer" className="inline-flex shrink-0">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-9 w-9 p-0 border-border/70 bg-background text-foreground hover:bg-muted hover:text-foreground hover:border-border font-semibold shadow-2xs"
                            >
                              <Printer className="h-4 w-4 text-primary" />
                            </Button>
                          </a>
                        </AppTooltip>
                        {canGenerateGatePass ? (
                          <AppTooltip label="Update Gate Pass Delivery">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="shrink-0 h-9 w-9 p-0 text-muted-foreground hover:bg-muted hover:text-foreground font-medium"
                              onClick={openGatePassModal}
                            >
                              <Ticket className="h-4 w-4" />
                            </Button>
                          </AppTooltip>
                        ) : null}
                      </>
                    ) : null}
                  </>
                );
              })()}
              {canRecordPayment && !isCancelled && sale.is_installment && (sale.down_payment_outstanding ?? 0) > 0 ? (
                <AppTooltip label={`Pay Down Payment (${formatCurrency(sale.down_payment_outstanding ?? 0)} due)`}>
                  <Button
                    type="button"
                    variant="premium"
                    size="sm"
                    className="shrink-0 h-9 w-9 p-0 shadow-xs"
                    onClick={() => setDownPaymentDialogOpen(true)}
                  >
                    <Wallet className="h-4 w-4" />
                  </Button>
                </AppTooltip>
              ) : null}
              {canRecordPayment && !isCancelled && !sale.is_installment && (sale.balance_outstanding ?? 0) > 0 ? (
                <AppTooltip label={`Record Payment (${formatCurrency(sale.balance_outstanding ?? 0)} due)`}>
                  <Button
                    type="button"
                    variant="premium"
                    size="sm"
                    className="shrink-0 h-9 w-9 p-0 shadow-xs"
                    onClick={() => setBalanceDialogOpen(true)}
                  >
                    <Wallet className="h-4 w-4" />
                  </Button>
                </AppTooltip>
              ) : null}
              {sale.product_id || sale.product?.id ? (
                <AppTooltip label="Product Ledger & Money Flow">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 h-9 w-9 p-0 border-border/70 bg-background text-foreground hover:bg-muted hover:text-foreground hover:border-border font-semibold shadow-2xs"
                    onClick={() => setLedgerModalOpen(true)}
                  >
                    <BookOpen className="h-4 w-4 text-primary" />
                  </Button>
                </AppTooltip>
              ) : null}
              <AppTooltip label="Download PDF Invoice">
                <a href={`/sales/${sale.id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex shrink-0">
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    className="shrink-0 h-9 w-9 p-0 font-semibold bg-sky-600 hover:bg-sky-700 text-white shadow-xs"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </a>
              </AppTooltip>
              {canCancelSale && !isCancelled && !isFullyPaid ? (
                <AppTooltip label="Cancel Sale">
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="shrink-0 h-9 w-9 p-0 font-semibold shadow-xs"
                    onClick={() => setCancelOpen(true)}
                  >
                    <Ban className="h-4 w-4" />
                  </Button>
                </AppTooltip>
              ) : null}
            </div>
          </div>
        </div>

        {/* ── 2. SALE OVERVIEW (HERO SECTION - PROMINENTLY ON TOP) ──── */}
        <section className="glass-card overflow-hidden border border-border/60 shadow-card">
          {/* Header Bar of Sale Overview */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/40 bg-muted/20 px-4 py-3 sm:px-5 lg:px-6">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-primary/25 bg-primary/10 text-primary">
                <Car className="h-4 w-4" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-bold text-foreground">Sale Overview</h2>
                  <span className="rounded-md border border-border/70 bg-background/90 px-2 py-0.5 text-xs font-mono font-bold text-foreground shadow-2xs">
                    Ref #{invoiceNo}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">Primary vehicle contract, specifications &amp; participants</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-border/60 bg-background/80 px-2.5 py-1 text-xs font-medium text-foreground">
                <Tag className="h-3 w-3 text-primary" />
                Deal: {dealTypeLabel}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-border/60 bg-background/80 px-2.5 py-1 text-xs font-medium text-foreground">
                <Sparkles className="h-3 w-3 text-primary" />
                Rule: {profitRuleLabel}
              </span>
            </div>
          </div>

          <div className="p-4 sm:p-5 lg:p-6 space-y-4">
            {/* Hero Vehicle Display Banner */}
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between rounded-2xl border border-border/60 bg-gradient-to-br from-card via-card to-muted/25 p-4 sm:p-5 shadow-2xs">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-5 min-w-0 flex-1">
                {/* Vehicle Thumbnail with Status Badge */}
                <div className="relative h-24 w-36 sm:h-28 sm:w-44 shrink-0 overflow-hidden rounded-xl border border-border/70 bg-muted shadow-sm group">
                  <ProductImageThumb
                    src={productImageUrl}
                    alt={sale.product?.name ?? "Product"}
                    iconClassName="h-8 w-8"
                  />
                  <div className="absolute top-2 left-2">
                    {(() => {
                      const badge = vehicleStatusBadge(sale);
                      return (
                        <span className={cn("rounded-full px-2.5 py-0.5 text-[10px] font-bold tracking-wide border shadow-2xs backdrop-blur-md", badge.className)}>
                          {badge.label}
                        </span>
                      );
                    })()}
                  </div>
                </div>

                {/* Vehicle Title & Metadata */}
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-primary flex items-center gap-1 bg-primary/10 border border-primary/20 px-2.5 py-0.5 rounded-md">
                      <Car className="h-3 w-3" />
                      Vehicle Sold
                    </span>
                    {sale.product?.inventory_status ? (
                      <span className="text-xs text-muted-foreground">
                        Inventory: <strong className="capitalize text-foreground font-semibold">{sale.product.inventory_status.replace(/_/g, " ")}</strong>
                      </span>
                    ) : null}
                  </div>

                  <div className="flex flex-wrap items-center gap-2.5">
                    {sale.product?.id ? (
                      <Link
                        to={`/products/${sale.product.id}`}
                        className="group/link inline-flex items-center gap-2 text-lg sm:text-xl font-bold text-foreground hover:text-primary transition-colors"
                        title="View vehicle catalog details"
                      >
                        <span className="truncate">{sale.product?.name ?? "—"}</span>
                        <ExternalLink className="h-4 w-4 shrink-0 opacity-60 group-hover/link:opacity-100 text-primary transition-opacity" />
                      </Link>
                    ) : (
                      <h3 className="text-lg sm:text-xl font-bold text-foreground">
                        {sale.product?.name ?? "—"}
                      </h3>
                    )}

                    {(sale.product_id || sale.product?.id) ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1.5 px-2.5 text-xs font-semibold border-border/70 bg-background/80 hover:bg-muted hover:text-foreground text-foreground shadow-2xs rounded-lg"
                        onClick={() => setLedgerModalOpen(true)}
                        title="Product money flow & vehicle ledger"
                      >
                        <BookOpen className="h-3.5 w-3.5 text-primary" />
                        Money Flow
                      </Button>
                    ) : null}
                  </div>

                  {/* Attributes Pills */}
                  {sale.product?.attributes && sale.product.attributes.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                      {sale.product.attributes.slice(0, 5).map((attr) => (
                        <span
                          key={attr.code}
                          className="rounded-md border border-border/50 bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
                        >
                          <span className="capitalize">{attr.code.replace(/_/g, " ")}</span>:{" "}
                          <span className="font-semibold text-foreground">{String(attr.value)}</span>
                        </span>
                      ))}
                      {sale.product.attributes.length > 5 ? (
                        <span className="text-[11px] text-muted-foreground self-center">
                          +{sale.product.attributes.length - 5} more
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Total Sale Price Highlight Block */}
              <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center border-t sm:border-t-0 sm:border-l border-border/50 pt-3 sm:pt-0 sm:pl-6 shrink-0">
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">
                  Agreed Sale Price
                </p>
                <div className="text-right">
                  <div className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-foreground tabular-nums tracking-tight">
                    {currSymbol} {formatAmount(sale.total_amount)}
                  </div>
                  <p className="text-xs font-semibold text-primary mt-0.5">
                    {pkrAmountShortLabel(sale.total_amount)}
                  </p>
                </div>
              </div>
            </div>

            {/* Stakeholder Trio Cards: Customer, Salesperson, Broker */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {/* Customer Card */}
              <div className="rounded-xl border border-border/50 bg-card p-3.5 shadow-2xs hover:border-border/80 transition-colors">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-sky-500/25 bg-sky-500/10 text-sky-600 dark:text-sky-400">
                    <User className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      Customer
                    </p>
                    <p className="truncate text-sm font-bold text-foreground mt-0.5">
                      {sale.customer?.name ?? "Walk-in Customer"}
                    </p>
                  </div>
                </div>
                <div className="mt-2.5 space-y-1 border-t border-border/30 pt-2 text-xs text-muted-foreground">
                  <div className="flex items-center justify-between">
                    <span>Phone</span>
                    <span className="font-medium text-foreground">{sale.customer?.phone || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>City</span>
                    <span className="font-medium text-foreground">{sale.customer?.city || "—"}</span>
                  </div>
                </div>
              </div>

              {/* Salesperson Card */}
              <div className="rounded-xl border border-border/50 bg-card p-3.5 shadow-2xs hover:border-border/80 transition-colors">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-emerald-500/25 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                    <UserCheck className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      Salesperson
                    </p>
                    <p className="truncate text-sm font-bold text-foreground mt-0.5">
                      {sale.employee?.full_name ?? "Direct Showroom Sale"}
                    </p>
                  </div>
                </div>
                <div className="mt-2.5 space-y-1 border-t border-border/30 pt-2 text-xs text-muted-foreground">
                  <div className="flex items-center justify-between">
                    <span>Agent Code</span>
                    <span className="font-mono font-medium text-foreground">{sale.employee?.employee_code || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Role</span>
                    <span className="font-medium text-foreground">{sale.employee?.full_name ? "Sales Agent" : "Direct Showroom"}</span>
                  </div>
                </div>
              </div>

              {/* Broker Card */}
              <div className="rounded-xl border border-border/50 bg-card p-3.5 shadow-2xs hover:border-border/80 transition-colors">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-amber-500/25 bg-amber-500/10 text-amber-600 dark:text-amber-400">
                    <Handshake className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      Broker / Agent
                    </p>
                    <p className="truncate text-sm font-bold text-foreground mt-0.5">
                      {sale.broker?.name ?? "Direct Sale (No Broker)"}
                    </p>
                  </div>
                </div>
                <div className="mt-2.5 space-y-1 border-t border-border/30 pt-2 text-xs text-muted-foreground">
                  <div className="flex items-center justify-between">
                    <span>Commission</span>
                    <span className="font-semibold text-foreground tabular-nums">
                      {sale.broker_commission != null && Number(sale.broker_commission) > 0
                        ? `${currSymbol} ${formatAmount(Number(sale.broker_commission))}`
                        : "None"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Charged To</span>
                    <span className="capitalize font-medium text-foreground">
                      {sale.broker_commission != null && Number(sale.broker_commission) > 0
                        ? brokerCommissionPayerLabel(sale.broker_commission_payer)
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes & Remarks callout */}
            {sale.description?.trim() ? (
              <div className="rounded-xl border border-border/50 bg-muted/20 p-3.5 flex items-start gap-3">
                <FileText className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Notes &amp; Deal Remarks
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-xs text-foreground/90 leading-relaxed font-normal">
                    {sale.description}
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        </section>

        {/* ── 3. OPERATIONAL SETTLEMENTS ("Payments" cards) ────────── */}
        {!isCancelled ? (
          <SectionCard
            title="Quick Settlements"
            icon={HandCoins}
            subtitle="Pending payouts & direct settlement actions"
            bodyClassName="p-3 sm:p-3.5"
          >
            {(() => {
              const primaryInvestor = distributions.find(
                (pd) => canRecordPayment && Number(pd.available ?? pd.balance ?? 0) > 0.009,
              );
              const investorDue = primaryInvestor
                ? Number(primaryInvestor.available ?? primaryInvestor.balance ?? 0)
                : 0;
              const brokerDue = Number(sale.broker_commission_outstanding ?? 0);
              const customerDue = sale.is_installment
                ? Number(sale.down_payment_outstanding ?? 0)
                : Number(sale.balance_outstanding ?? sale.outstanding ?? 0);
              const investorLabel =
                sale.product?.deal_type === "owner_vehicle" ? "Pay Investor" : "Pay Partner";

              return (
                <div className="grid grid-cols-1 gap-2.5 md:grid-cols-3 items-stretch">
                  {/* Broker Card */}
                  <div className="flex flex-col justify-between rounded-xl border border-border/50 bg-card p-3 shadow-2xs hover:border-border/80 transition-colors gap-2.5">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-amber-500/25 bg-amber-500/10 text-amber-600 dark:text-amber-400">
                            <Handshake className="h-3.5 w-3.5" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground truncate">
                                Broker Commission
                              </span>
                            </div>
                            <p className="truncate text-xs font-bold text-foreground">
                              {sale.broker?.name ?? "No broker"}
                            </p>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground block">
                            Total
                          </span>
                          <span className="text-xs font-bold text-foreground tabular-nums">
                            {formatCurrency(Number(sale.broker_commission ?? 0))}
                          </span>
                        </div>
                      </div>

                      {/* Compact Paid vs Due Strip */}
                      <div className="flex items-center justify-between rounded-lg bg-muted/40 px-2.5 py-1 text-xs border border-border/30">
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-muted-foreground font-medium">Paid:</span>
                          <span className="font-semibold text-foreground tabular-nums text-xs">
                            {formatCurrency(Number(sale.broker_commission_paid ?? 0))}
                          </span>
                        </div>
                        <div className="h-3 w-px bg-border/60" />
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-muted-foreground font-medium">Due:</span>
                          <span className={cn(
                            "font-bold tabular-nums text-xs",
                            brokerDue > 0.009 ? "text-amber-700 dark:text-amber-400" : "text-emerald-700 dark:text-emerald-400"
                          )}>
                            {brokerDue > 0.009 ? formatCurrency(brokerDue) : "Settled ✓"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      {canRecordPayment && brokerDue > 0.009 ? (
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 w-full gap-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 shadow-2xs"
                          onClick={() => setBrokerPaymentDialogOpen(true)}
                        >
                          <HandCoins className="h-3.5 w-3.5" />
                          Pay Broker ({currSymbol} {formatAmount(brokerDue)})
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 w-full gap-1.5 text-xs font-medium rounded-lg border-border/40 bg-muted/20 text-muted-foreground hover:bg-muted/20 hover:text-muted-foreground cursor-not-allowed opacity-75"
                          disabled
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                          {brokerDue <= 0.009 ? "Broker Settled" : "Pay Broker"}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Investor / Owner Card */}
                  <div className="flex flex-col justify-between rounded-xl border border-border/50 bg-card p-3 shadow-2xs hover:border-border/80 transition-colors gap-2.5">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-sky-500/25 bg-sky-500/10 text-sky-600 dark:text-sky-400">
                            <CreditCard className="h-3.5 w-3.5" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground truncate">
                                {sale.product?.deal_type === "owner_vehicle" ? "Investor / Owner" : "Deal Partner"}
                              </span>
                            </div>
                            <p className="truncate text-xs font-bold text-foreground">
                              {primaryInvestor?.party_name
                                || distributions[0]?.party_name
                                || (distributions.length > 0 ? "Deal partner" : "No investor")}
                            </p>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground block">
                            Payable
                          </span>
                          <span className="text-xs font-bold text-foreground tabular-nums">
                            {formatCurrency(
                              primaryInvestor
                                ? Number(primaryInvestor.final_payable ?? 0)
                                : Number(distributions[0]?.final_payable ?? 0)
                            )}
                          </span>
                        </div>
                      </div>

                      {/* Compact Paid vs Due Strip */}
                      <div className="flex items-center justify-between rounded-lg bg-muted/40 px-2.5 py-1 text-xs border border-border/30">
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-muted-foreground font-medium">Paid:</span>
                          <span className="font-semibold text-foreground tabular-nums text-xs">
                            {formatCurrency(Number((primaryInvestor ?? distributions[0])?.paid_amount ?? 0))}
                          </span>
                        </div>
                        <div className="h-3 w-px bg-border/60" />
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-muted-foreground font-medium">Due:</span>
                          <span className={cn(
                            "font-bold tabular-nums text-xs",
                            investorDue > 0.009 ? "text-amber-700 dark:text-amber-400" : "text-emerald-700 dark:text-emerald-400"
                          )}>
                            {investorDue > 0.009 ? formatCurrency(investorDue) : "Settled ✓"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      {canRecordPayment && primaryInvestor && investorDue > 0.009 ? (
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 w-full gap-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 shadow-2xs"
                          onClick={() => openInvestorPayDialog(primaryInvestor)}
                        >
                          <CreditCard className="h-3.5 w-3.5" />
                          {investorLabel} ({currSymbol} {formatAmount(investorDue)})
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 w-full gap-1.5 text-xs font-medium rounded-lg border-border/40 bg-muted/20 text-muted-foreground hover:bg-muted/20 hover:text-muted-foreground cursor-not-allowed opacity-75"
                          disabled
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                          {investorDue <= 0.009 ? "Partner Settled" : investorLabel}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Customer Balance / Down Payment Card */}
                  <div className="flex flex-col justify-between rounded-xl border border-border/50 bg-card p-3 shadow-2xs hover:border-border/80 transition-colors gap-2.5">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-emerald-500/25 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                            <Wallet className="h-3.5 w-3.5" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground truncate">
                                {sale.is_installment ? "Customer DP" : "Customer Balance"}
                              </span>
                            </div>
                            <p className="truncate text-xs font-bold text-foreground">
                              {sale.customer?.name ?? "Walk-in Customer"}
                            </p>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground block">
                            Outstanding
                          </span>
                          <span className={cn(
                            "text-xs font-bold tabular-nums",
                            customerDue > 0.009 ? "text-amber-700 dark:text-amber-400" : "text-emerald-700 dark:text-emerald-400"
                          )}>
                            {currSymbol} {formatAmount(customerDue)}
                          </span>
                        </div>
                      </div>

                      {/* Compact Paid vs Due Strip */}
                      <div className="flex items-center justify-between rounded-lg bg-muted/40 px-2.5 py-1 text-xs border border-border/30">
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-muted-foreground font-medium">
                            {sale.is_installment ? "DP Paid:" : "Paid:"}
                          </span>
                          <span className="font-semibold text-foreground tabular-nums text-xs">
                            {formatCurrency(
                              sale.is_installment
                                ? Number(sale.down_payment_paid ?? sale.token_amount ?? 0)
                                : Number(sale.balance_paid_total ?? 0),
                            )}
                          </span>
                        </div>
                        <div className="h-3 w-px bg-border/60" />
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-muted-foreground font-medium">Due:</span>
                          <span className={cn(
                            "font-bold tabular-nums text-xs",
                            customerDue > 0.009 ? "text-amber-700 dark:text-amber-400" : "text-emerald-700 dark:text-emerald-400"
                          )}>
                            {customerDue > 0.009 ? formatCurrency(customerDue) : "Cleared ✓"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      {canRecordPayment && customerDue > 0.009 ? (
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 w-full gap-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 shadow-2xs"
                          onClick={() => {
                            if (sale.is_installment) {
                              setDownPaymentDialogOpen(true);
                            } else {
                              setBalanceDialogOpen(true);
                            }
                          }}
                        >
                          <Wallet className="h-3.5 w-3.5" />
                          Record Payment ({currSymbol} {formatAmount(customerDue)})
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 w-full gap-1.5 text-xs font-medium rounded-lg border-border/40 bg-muted/20 text-muted-foreground hover:bg-muted/20 hover:text-muted-foreground cursor-not-allowed opacity-75"
                          disabled
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                          {customerDue <= 0.009 ? "Fully Collected" : "Record Payment"}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </SectionCard>
        ) : null}

        {/* ── 5. FINANCIAL CORE (ROW 1: TWO BALANCED COLUMNS) ──────── */}
        <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2 items-stretch">
          {/* Column 1: Deal Economics & Profitability */}
          <SectionCard
            title="Deal Economics & Profitability"
            icon={TrendingUp}
            action={
              <div className="flex flex-wrap items-center gap-1.5 justify-end">
                {netProfitValue > 0.009 ? (
                  <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-bold text-emerald-700 dark:text-emerald-400">
                    Net Profit {sale.total_amount ? `(${((netProfitValue / Number(sale.total_amount)) * 100).toFixed(1)}%)` : ""}
                  </span>
                ) : netProfitValue < -0.009 ? (
                  <span className="rounded-full border border-rose-500/30 bg-rose-500/10 px-2.5 py-0.5 text-xs font-bold text-rose-700 dark:text-rose-400">
                    Net Loss
                  </span>
                ) : (
                  <span className="rounded-full border border-border/60 bg-muted/40 px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
                    Break-even
                  </span>
                )}
              </div>
            }
          >
            {isOwnerSurplusSummary(summary) ? (
              <div className="space-y-2.5">
                {summary?.owner_payable ? (
                  <div className="rounded-xl border border-border/40 bg-muted/10 p-3 space-y-1">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                      Owner Consignment Agreement
                    </p>
                    <SummaryRow
                      label="Owner demand price"
                      value={<MoneyAmount value={summary.owner_payable.owner_demand_price} emphasis />}
                    />
                    <SummaryRow
                      label="Total owner payable"
                      value={<MoneyAmount value={summary.owner_payable.total_payable} emphasis />}
                      borderTop
                      emphasis
                    />
                  </div>
                ) : null}
                {summary?.surplus_distribution ? (
                  <div className="rounded-xl border border-border/40 bg-muted/10 p-3 space-y-1">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                      Surplus Split
                    </p>
                    <SummaryRow
                      label="Surplus (Sale − Owner Demand)"
                      value={<MoneyAmount value={summary.surplus_distribution.surplus} emphasis />}
                    />
                    <SummaryRow
                      label={`Showroom (${formatRatio(summary.surplus_distribution.showroom_percent)})`}
                      value={<MoneyAmount value={summary.surplus_distribution.showroom_amount} />}
                    />
                  </div>
                ) : null}
                {summary?.profit_summary ? (
                  <div className="rounded-xl border border-border/40 bg-muted/10 p-3 space-y-1">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                      Profit Reconciliation
                    </p>
                    <SummaryRow
                      label="Purchase cost"
                      value={<MoneyAmount value={summary.profit_summary.purchase_cost} />}
                      muted
                    />
                    <SummaryRow
                      label="Additional costs"
                      value={<MoneyAmount value={summary.profit_summary.additional_costs ?? summary.additional_costs ?? 0} />}
                      muted
                    />
                    <SummaryRow
                      label="Owner actual profit"
                      value={<MoneyAmount value={summary.profit_summary.owner_actual_profit} emphasis />}
                    />
                    <SummaryRow
                      label="Showroom profit"
                      value={<MoneyAmount value={summary.profit_summary.showroom_profit} emphasis />}
                    />
                    <SummaryRow
                      label="Total profit"
                      value={<MoneyAmount value={summary.profit_summary.total_profit} emphasis />}
                      borderTop
                    />
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="space-y-1.5 flex flex-col justify-between h-full">
                <div className="space-y-1.5">
                  <SummaryRow
                    label="Agreed Sale Price"
                    value={<MoneyAmount value={Number(summary?.sale_price ?? sale.total_amount)} emphasis />}
                  />
                  <SummaryRow
                    label="Vehicle Purchase Cost"
                    value={<MoneyAmount value={Number(summary?.purchase_cost ?? sale.product?.purchase_price ?? 0)} />}
                  />
                  <SummaryRow
                    label="Additional Costs / Expenses"
                    value={<MoneyAmount value={Number(summary?.additional_costs ?? 0)} />}
                  />

                  {showOverallBrokerInDealTotals ? (
                    <>
                      <SummaryRow
                        label="Gross Profit"
                        value={<MoneyAmount value={grossProfitValue} emphasis />}
                        borderTop
                      />
                      <SummaryRow
                        label="Broker Commission (Overall Net)"
                        value={<MoneyAmount value={overallBrokerAmount} />}
                      />
                    </>
                  ) : null}

                  {/* Highlighted Net Profit / Net Loss Box */}
                  <div className="my-2 flex min-w-0 items-center justify-between gap-2 rounded-xl border border-border/50 bg-muted/25 px-3.5 py-2.5">
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground block">
                        Deal Bottom Line
                      </span>
                      <span className="text-sm font-bold text-foreground">
                        {netProfitValue < -0.009 ? "Net Deal Loss" : netProfitValue > 0.009 ? "Net Deal Profit" : "Net Deal Result"}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className={cn(
                        "text-lg sm:text-xl font-extrabold tabular-nums block",
                        netProfitValue < -0.009 ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"
                      )}>
                        {currSymbol} {formatAmount(netProfitValue)}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        Margin: {sale.total_amount ? `${((netProfitValue / Number(sale.total_amount)) * 100).toFixed(1)}%` : "0%"}
                      </span>
                    </div>
                  </div>

                  {summary?.investor_surplus != null && Number(summary.investor_surplus) > 0.009 ? (
                    <SummaryRow
                      label="Investor Surplus"
                      value={<MoneyAmount value={Number(summary.investor_surplus)} />}
                    />
                  ) : null}

                  {summary?.extra_profit != null && Number(summary.extra_profit) > 0.009 ? (
                    <div className="flex items-center justify-between gap-2 py-1 text-sm">
                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                        Extra profit recoveries
                        <button
                          type="button"
                          onClick={() => setExtraProfitDetailsOpen(true)}
                          className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-border/60 text-muted-foreground hover:text-foreground"
                          aria-label="View extra profit details"
                          title="View extra profit details"
                        >
                          <Plus className="h-2.5 w-2.5" strokeWidth={3} />
                        </button>
                      </span>
                      <span className="font-semibold text-foreground tabular-nums">
                        {currSymbol} {formatAmount(Number(summary.extra_profit))}
                      </span>
                    </div>
                  ) : null}
                </div>

                <div className="pt-2 border-t border-border/30 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="text-[11px] font-medium">
                    {netProfitValue < -0.009
                      ? Math.abs(dealTotalsFinalDistribution - netProfitValue) < 0.01
                        ? "Loss fully allocated ✓"
                        : "Loss not fully allocated"
                      : netProfitValue > 0.009
                        ? "100% Fully Distributed ✓"
                        : "Break-even"}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <span>Pool:</span>
                    <span className="font-bold text-foreground tabular-nums">
                      {currSymbol} {formatAmount(dealTotalsFinalDistribution)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </SectionCard>

          {/* Column 2: Customer Inflow & Balance */}
          <SectionCard
            title="Customer Inflow & Balance"
            icon={Wallet}
            action={
              <div className="flex flex-wrap items-center gap-1.5 justify-end">
                <PaymentStatusBadge status={sale.payment_status} />
                {sale.token_amount != null && Number(sale.token_amount) > 0 ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">
                    ✓ Token Verified
                  </span>
                ) : null}
              </div>
            }
          >
            <div className="space-y-1.5 flex flex-col justify-between h-full">
              <div className="space-y-1.5">
                {/* Collection Velocity Progress Bar */}
                <div className="rounded-xl border border-border/50 bg-muted/20 p-2.5 my-1.5 space-y-1.5">
                  <div className="flex items-center justify-between text-xs font-semibold">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      Collection Velocity
                    </span>
                    <span className={cn("text-xs font-bold tabular-nums", percentPaid >= 100 ? "text-emerald-600 dark:text-emerald-400" : "text-primary")}>
                      {percentPaid}% of {currSymbol} {formatAmount(totalSaleAmount)}
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-muted border border-border/30">
                    <div
                      className={cn(
                        "h-full transition-all duration-700 rounded-full",
                        percentPaid >= 100
                          ? "bg-emerald-500"
                          : percentPaid > 0
                            ? "bg-gradient-to-r from-primary/70 to-primary"
                            : "bg-muted-foreground/30",
                      )}
                      style={{ width: `${Math.min(100, Math.max(0, percentPaid))}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>
                      Collected:{" "}
                      <strong className="text-emerald-600 dark:text-emerald-400 font-semibold tabular-nums">
                        {currSymbol} {formatAmount(paidTotalAmount)}
                      </strong>
                    </span>
                    <span>
                      Remaining:{" "}
                      <strong className={cn("font-semibold tabular-nums", (totalSaleAmount - paidTotalAmount) <= 0.009 ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400")}>
                        {currSymbol} {formatAmount(Math.max(0, totalSaleAmount - paidTotalAmount))}
                      </strong>
                    </span>
                  </div>
                </div>

                <SummaryRow label="Total Contract Value" value={<MoneyAmount value={sale.total_amount} emphasis />} />

                {sale.broker_commission != null && Number(sale.broker_commission) > 0 ? (
                  <SummaryRow
                    label={`Broker commission (${brokerCommissionPayerLabel(sale.broker_commission_payer)})`}
                    value={<MoneyAmount value={Number(sale.broker_commission)} />}
                  />
                ) : null}

                {/* Booking Token line */}
                {sale.token_amount != null && Number(sale.token_amount) > 0 ? (
                  <div className="my-1.5 flex min-w-0 items-center justify-between gap-3 rounded-xl border border-border/50 bg-muted/20 px-3.5 py-2">
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                      <span className="text-xs font-semibold text-foreground">Booking Token</span>
                      <span className="rounded-md border border-border/60 bg-background/80 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                        {tokenTypeLabel(sale.token_type)}
                      </span>
                    </div>
                    <div className="shrink-0 text-right">
                      <span className="text-xs font-bold text-foreground tabular-nums">
                        {formatCurrency(Number(sale.token_amount))}
                      </span>
                      <p className="text-[10px] text-muted-foreground">
                        {paymentMethodLabel(sale.payment_method)}
                        {sale.date && isValid(parseISO(sale.date)) ? ` • ${format(parseISO(sale.date), "MMM d, yyyy")}` : ""}
                      </p>
                    </div>
                  </div>
                ) : null}

                {/* For Installment Sales: Down Payment card */}
                {sale.is_installment ? (
                  <div className="my-1.5 rounded-xl border border-border/60 bg-muted/20 p-2.5 space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-foreground">Agreed Down Payment</span>
                      <MoneyAmount value={sale.down_payment ?? 0} emphasis />
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Down Payment Paid Realized</span>
                      <MoneyAmount
                        value={sale.down_payment_paid ?? (sale.token_amount ?? 0)}
                        amountClassName="text-emerald-600 dark:text-emerald-400 font-semibold"
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs pt-1 border-t border-border/40">
                      <span className="font-semibold text-foreground">Remaining Down Payment</span>
                      <MoneyAmount
                        value={sale.down_payment_outstanding ?? 0}
                        emphasis
                        amountClassName={
                          Number(sale.down_payment_outstanding ?? 0) <= 0.009
                            ? "text-emerald-600 dark:text-emerald-400 font-bold"
                            : "text-amber-600 dark:text-amber-400 font-bold"
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <SummaryRow label="Balance Payable" value={<MoneyAmount value={balanceAmount} emphasis />} />
                )}

                {/* Balance paid total */}
                {!sale.is_installment && sale.balance_paid_total != null && Number(sale.balance_paid_total) > 0 ? (
                  <SummaryRow
                    label="Balance Paid Realized"
                    value={<MoneyAmount value={sale.balance_paid_total} amountClassName="text-emerald-600 dark:text-emerald-400 font-semibold" />}
                  />
                ) : null}

                {/* Installment totals */}
                {sale.is_installment && installmentOutstanding != null ? (
                  <>
                    <SummaryRow
                      label="Financed Principal (Installments)"
                      value={<MoneyAmount value={sale.balance_amount ?? 0} emphasis />}
                    />
                    <SummaryRow
                      label="Installments Paid Total"
                      value={<MoneyAmount value={sale.installment_paid_total ?? 0} />}
                    />
                    <SummaryRow
                      label="Installments Outstanding"
                      value={<MoneyAmount value={installmentOutstanding} emphasis />}
                      emphasis
                    />
                  </>
                ) : null}
              </div>

              {/* Remaining balance due callout */}
              <div className="pt-2 border-t border-border/30">
                <div className="flex min-w-0 items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <span className="font-bold text-foreground text-sm block">
                      {sale.is_installment ? "Total Remaining Due" : "Remaining Balance Due"}
                    </span>
                    {sale.payable_date ? (
                      <p className="text-[11px] text-muted-foreground">Due Date: {sale.payable_date}</p>
                    ) : null}
                  </div>
                  <span className="shrink-0 text-right">
                    <MoneyAmount
                      value={sale.outstanding ?? (sale.is_installment ? installmentOutstanding : (sale.balance_outstanding ?? 0))}
                      emphasis
                      amountClassName={
                        Number(sale.outstanding ?? (sale.is_installment ? installmentOutstanding : (sale.balance_outstanding ?? 0))) <= 0.009
                          ? "text-emerald-600 dark:text-emerald-400 font-extrabold text-base sm:text-lg"
                          : "text-amber-600 dark:text-amber-400 font-extrabold text-base sm:text-lg"
                      }
                    />
                  </span>
                </div>
              </div>
            </div>
          </SectionCard>
        </div>

        {/* ── 6. STAKEHOLDER & PARTNER DISTRIBUTION (ROW 2: FULL WIDTH) ──── */}
        <SectionCard
          title="Stakeholder & Partner Distribution"
          icon={Handshake}
          subtitle="Dealership showroom share, partner allocations & payout balances"
          action={
            <div className="flex flex-wrap items-center justify-start gap-1.5 sm:justify-end">
              {summary?.distribution_total != null ? (
                <span className="rounded-full border border-border/60 bg-muted/40 px-2.5 py-0.5 text-xs font-semibold text-foreground tabular-nums">
                  {distributionTotalValue < -0.009 ? "Loss Pool" : "Distribution Pool"}: {currSymbol} {formatAmount(Math.abs(distributionTotalValue))}
                </span>
              ) : null}
              {summaryPartners.length > 0 ? (
                <span className="rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs font-bold text-primary">
                  {summaryPartners.length} Partner{summaryPartners.length > 1 ? "s" : ""}
                </span>
              ) : null}
            </div>
          }
        >
          {!hasSummaryRecipients ? (
            <div className="rounded-lg border border-dashed border-border/60 bg-muted/10 px-3 py-6 text-center">
              <p className="text-sm font-medium text-foreground">No profit distribution recorded</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Updates automatically from deal type, profit rule, sale price, costs, and partners.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 items-stretch">
              {/* Card 1: Showroom Share */}
              <div className="flex flex-col justify-between rounded-xl border border-border/50 bg-card p-3.5 shadow-2xs hover:border-border/80 transition-colors gap-3">
                <div className="space-y-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-primary/25 bg-primary/10 text-primary">
                        <Building2 className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block truncate">
                          Showroom Share
                        </span>
                        <p className="truncate text-sm font-bold text-foreground">
                          {summary?.showroom?.name ?? "Dealership Showroom"}
                        </p>
                        <p className="text-[11px] text-muted-foreground truncate">
                          Commission &amp; Retainer
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-xs font-semibold text-muted-foreground block">
                        {formatRatio(summary?.showroom?.percent)}
                      </span>
                      <span className="text-base font-extrabold text-foreground tabular-nums">
                        {currSymbol}{" "}
                        {formatAmount(
                          Number(recipientShareAmount(summary?.showroom, summary) ?? 0) +
                            Number(summary?.extra_profit ?? 0),
                        )}
                      </span>
                    </div>
                  </div>

                  {summary?.investor_surplus != null && Number(summary.investor_surplus) > 0.009 ? (
                    <div className="rounded-lg bg-muted/30 px-2.5 py-1 text-[11px] text-muted-foreground">
                      Investor surplus: {currSymbol} {formatAmount(Number(summary.investor_surplus))}
                    </div>
                  ) : null}

                  {summary?.extra_profit != null && Number(summary.extra_profit) > 0.009 ? (
                    <button
                      type="button"
                      onClick={() => setExtraProfitDetailsOpen(true)}
                      className="text-left text-[11px] text-primary underline-offset-2 hover:underline font-semibold block"
                    >
                      + Extra profit recoveries: {currSymbol} {formatAmount(Number(summary.extra_profit))}
                    </button>
                  ) : null}

                  {(() => {
                    const deducted = Number(
                      summary?.showroom?.broker_commission_deducted ??
                        (summary?.broker_commission_payer === "showroom"
                          ? summary?.broker_commission
                          : 0) ??
                        0,
                    );
                    if (deducted <= 0.009 || !summary?.showroom) return null;
                    const after = Number(recipientShareAmount(summary.showroom, summary) ?? 0);
                    const before = Number(
                      recipientShareBeforeBroker(summary.showroom, summary) ?? after + deducted,
                    );
                    return (
                      <BrokerCommissionCalc
                        before={before}
                        deducted={deducted}
                        after={after}
                        currSymbol={currSymbol}
                      />
                    );
                  })()}
                </div>

                <div className="border-t border-border/30 pt-2 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="text-[11px]">Allocation Status</span>
                  <span className="inline-flex items-center gap-1 font-semibold text-emerald-700 dark:text-emerald-400 text-xs">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Retained in Dealership
                  </span>
                </div>
              </div>

              {/* Cards 2..N: Partner Allocations */}
              {summaryPartners.map((row, idx) => {
                const dbDist = distributions.find((d) => d.partner_id === row.partner_id);
                const payable = row.final_payable ?? dbDist?.final_payable;
                const paid = dbDist?.paid_amount ?? (row as { paid_amount?: number }).paid_amount;
                const balance = Number(payable ?? 0) - Number(paid ?? 0);
                const amount = recipientShareAmount(row, summary);
                const rawNote = (row.note ?? "").trim();
                const cleanedNote = rawNote
                  .replace(/\s*\.?\s*Less broker commission.*$/i, "")
                  .replace(/\s*\.?\s*Broker commission \(seller → showroom fallback\).*$/i, "")
                  .replace(/(Showroom commission)\d+/i, "$1")
                  .trim()
                  .replace(/\.+$/, "");
                const remainingSaleNote =
                  "Receives the remaining sale amount after Showroom commission";
                const noteText = /Receives the remaining sale amount after Showroom commission/i.test(
                  cleanedNote,
                )
                  ? remainingSaleNote
                  : cleanedNote ||
                    (row.commission_type
                      ? String(row.commission_type)
                      : idx === 0
                        ? "Primary vehicle investor"
                        : "Co-investor");

                const isSettled = balance <= 0.009;

                return (
                  <div
                    key={row.partner_id ?? row.name}
                    className="flex flex-col justify-between rounded-xl border border-border/50 bg-card p-3.5 shadow-2xs hover:border-border/80 transition-colors gap-3"
                  >
                    <div className="space-y-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-sky-500/25 bg-sky-500/10 text-sky-600 dark:text-sky-400">
                            <UserCheck className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block truncate">
                              Partner #{idx + 1}
                            </span>
                            <p className="truncate text-sm font-bold text-foreground">
                              {row.name}
                            </p>
                            <p className="text-[11px] text-muted-foreground truncate">
                              {noteText}
                            </p>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="text-xs font-semibold text-muted-foreground block tabular-nums">
                            {formatRatio(row.profit_ratio ?? row.percent ?? row.purchase_ratio)} share
                          </span>
                          <span className="text-base font-extrabold text-foreground tabular-nums">
                            {currSymbol} {formatAmount(Number(amount ?? 0))}
                          </span>
                        </div>
                      </div>

                      {/* Broker commission deduction if applicable */}
                      {(() => {
                        const deducted = Number(row.broker_commission_deducted ?? 0);
                        if (deducted <= 0.009) return null;
                        const after = Number(amount ?? 0);
                        const before = Number(
                          recipientShareBeforeBroker(row, summary) ?? after + deducted,
                        );
                        return (
                          <BrokerCommissionCalc
                            before={before}
                            deducted={deducted}
                            after={after}
                            currSymbol={currSymbol}
                          />
                        );
                      })()}

                      {/* 3-Column Metric Bar: Payable | Paid | Balance */}
                      <div className="grid grid-cols-3 gap-2 rounded-lg bg-muted/40 p-2 text-center border border-border/30">
                        <div>
                          <span className="block text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                            Payable
                          </span>
                          <span className="font-bold text-foreground text-xs tabular-nums">
                            {currSymbol} {formatAmount(Number(payable ?? 0))}
                          </span>
                        </div>
                        <div>
                          <span className="block text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                            Paid
                          </span>
                          <span className="font-semibold text-foreground text-xs tabular-nums">
                            {currSymbol} {formatAmount(Number(paid ?? 0))}
                          </span>
                        </div>
                        <div>
                          <span className="block text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                            Balance
                          </span>
                          <span className={cn(
                            "font-bold text-xs tabular-nums",
                            isSettled ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"
                          )}>
                            {isSettled ? "Settled ✓" : `${currSymbol} ${formatAmount(Number(balance ?? 0))}`}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-border/30 pt-2 flex items-center justify-between text-xs text-muted-foreground">
                      <span className="text-[11px]">Payout Status</span>
                      <span className={cn(
                        "inline-flex items-center gap-1 font-semibold text-xs",
                        isSettled ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"
                      )}>
                        {isSettled ? (
                          <>
                            <CheckCircle2 className="h-3.5 w-3.5" /> Fully Settled
                          </>
                        ) : (
                          <>
                            <CreditCard className="h-3.5 w-3.5" /> Pending Balance
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>

        {/* ── 5. INSTALLMENT SCHEDULE ──────────────────────────────────── */}
        {sale.installments && sale.installments.length > 0 ? (
          <SectionCard
            title="Installment Schedule"
            icon={Wallet}
            action={
              installmentOutstanding != null && installmentOutstanding > 0 ? (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                    Remaining Outstanding
                  </p>
                  <MoneyAmount value={installmentOutstanding} emphasis />
                </div>
              ) : (
                <span className="inline-flex rounded-full bg-emerald-500/15 border border-emerald-500/30 px-3 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-400">
                  Fully Paid
                </span>
              )
            }
          >
            <p className="-mt-1 mb-3 text-xs text-muted-foreground">
              {installmentStats
                ? `${installmentStats.paidCount} of ${installmentStats.total} paid · ${sale.installment_count ?? sale.installments.length} total installments`
                : `${sale.installment_count ?? sale.installments.length} total installments`}
            </p>

            {canRecordPayment && !isCancelled && (sale.down_payment_outstanding ?? 0) > 0 ? (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3.5">
                <div className="flex items-center gap-2.5 min-w-0">
                  <HandCoins className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-foreground">
                      Down Payment Pending: {formatCurrency(sale.down_payment_outstanding ?? 0)}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      Booking token ({formatCurrency(sale.down_payment_paid ?? sale.token_amount ?? 0)}) was paid against the agreed down payment of {formatCurrency(sale.down_payment ?? 0)}.
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="premium"
                  size="sm"
                  className="h-8 text-xs font-semibold gap-1.5 shrink-0"
                  onClick={() => setDownPaymentDialogOpen(true)}
                >
                  <Wallet className="h-3.5 w-3.5" />
                  Pay Remaining Down Payment
                </Button>
              </div>
            ) : null}

            <div className="-mx-4 overflow-x-auto sm:-mx-5 rounded-xl border border-border/40 bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="border-b border-border/40 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-2.5 text-left w-12">#</th>
                    <th className="px-4 py-2.5 text-left">Due date</th>
                    <th className="px-4 py-2.5 text-left">Payment date</th>
                    <th className="px-4 py-2.5 text-right">Amount</th>
                    <th className="px-4 py-2.5 text-right">Paid</th>
                    <th className="px-4 py-2.5 text-right">Remaining</th>
                    <th className="px-4 py-2.5 text-center">Status</th>
                    <th className="px-4 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/20">
                  {sale.installments.map((row: SaleInstallmentRecord) => {
                    const due = parseISO(row.due_date);
                    const paidDate = row.paid_at && isValid(parseISO(row.paid_at)) ? parseISO(row.paid_at) : null;
                    const remaining = installmentRemaining(row);
                    const canPay = row.status !== "paid";
                    return (
                      <tr key={row.id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-2.5 tabular-nums text-xs font-medium text-muted-foreground">{row.installment_no}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-xs font-semibold text-foreground">
                          {isValid(due) ? format(due, "PP") : row.due_date}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-xs text-foreground">
                          {paidDate ? (
                            <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                              {format(paidDate, "PP")}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <MoneyAmount value={row.amount} />
                          {(row.penalty_amount ?? 0) > 0.009 ? (
                            <p className="text-[10px] text-rose-600 dark:text-rose-400 mt-0.5">
                              + Penalty {Number(row.penalty_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <MoneyAmount
                            value={Number(row.paid_amount || 0) + Number(row.penalty_paid || 0)}
                            amountClassName="font-normal text-muted-foreground"
                          />
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <MoneyAmount value={remaining} emphasis={remaining > 0} />
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold tracking-wide border",
                              row.status === "paid"
                                ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                                : row.status === "overdue"
                                  ? "border-rose-500/30 bg-rose-500/15 text-rose-700 dark:text-rose-400"
                                  : "border-amber-500/30 bg-amber-500/15 text-amber-700 dark:text-amber-400",
                            )}
                          >
                            {installmentStatusLabel(row.status)}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {canRecordPayment && !isCancelled && canPay ? (
                            <Button
                              type="button"
                              variant="premium"
                              size="sm"
                              className="h-7 text-xs font-semibold px-2.5"
                              onClick={() => setPaymentTarget(row)}
                            >
                              Record Payment
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground font-medium">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </SectionCard>
        ) : null}

        {/* ── 6. PAYMENT & INSTALLMENT LEDGER ──────────────────────────── */}
        {sale.payments && sale.payments.length > 0 ? (
          <div className="glass-card overflow-hidden p-5 sm:p-6 space-y-5">
            {/* Header: Title + Subtitle on left, Total Collected + Record Payment on right */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-blue-500/20 bg-blue-500/10 text-blue-600 dark:text-blue-400 mt-0.5">
                  <History className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h2 className="text-base font-bold text-foreground sm:text-lg">
                      Payment &amp; Installment Ledger
                    </h2>
                    <span className="rounded-full border border-border/60 bg-muted/40 px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
                      {sale.payments.length} {sale.payments.length === 1 ? "Entry" : "Entries"}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Real-time audit log of booking token and subsequent balance payments
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 sm:justify-end">
                <div className="text-left sm:text-right">
                  <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">TOTAL COLLECTED</p>
                  <div className="flex items-baseline gap-1.5 sm:justify-end">
                    <span className="text-lg font-extrabold text-foreground tabular-nums">
                      {currSymbol} {formatAmount(paidTotalAmount)}
                    </span>
                    <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">({percentPaid}%)</span>
                  </div>
                </div>
                {canRecordPayment && !isCancelled && !sale.is_installment && (sale.outstanding ?? sale.balance_outstanding ?? 0) > 0 ? (
                  <Button
                    type="button"
                    size="sm"
                    className="h-9 gap-1.5 text-xs font-bold px-4 bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white shadow-xs rounded-lg"
                    onClick={() => setBalanceDialogOpen(true)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Record Payment
                  </Button>
                ) : null}
              </div>
            </div>

            {/* Search & Filter Bar */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-1">
              {/* Search input */}
              <div className="relative w-full sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search memo, bank, receipt #..."
                  value={ledgerSearch}
                  onChange={(e) => setLedgerSearch(e.target.value)}
                  className="h-9 w-full rounded-lg border border-border/70 bg-muted/20 pl-9 pr-3 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40 transition-colors"
                />
              </div>

              {/* Method filter pills */}
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mr-1">METHOD:</span>
                <button
                  type="button"
                  onClick={() => setLedgerMethodFilter("all")}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-xs font-bold transition-colors",
                    ledgerMethodFilter === "all"
                      ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 shadow-xs"
                      : "bg-background text-muted-foreground hover:bg-muted hover:text-foreground border border-border/60",
                  )}
                >
                  All
                </button>
                {paymentMethods.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setLedgerMethodFilter(m)}
                    className={cn(
                      "rounded-md px-3 py-1.5 text-xs font-semibold transition-colors capitalize",
                      ledgerMethodFilter === m
                        ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 shadow-xs"
                        : "bg-background text-muted-foreground hover:bg-muted hover:text-foreground border border-border/60",
                    )}
                  >
                    {paymentMethodLabel(m)}
                  </button>
                ))}
              </div>
            </div>

            {/* Seamless Table */}
            <div className="-mx-5 overflow-x-auto sm:-mx-6 border-t border-border/40 pt-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/40 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    <th className="px-5 py-3 text-left sm:px-6">Date / Time</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">Method &amp; Channel</th>
                    <th className="px-4 py-3 text-left">Reference / Memo</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-5 py-3 text-right sm:px-6">Receipt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/25">
                  {filteredPayments.map((row: SalePaymentRecord, idx: number) => {
                    const date = row.payment_date ? parseISO(row.payment_date) : null;
                    const createdAt = row.created_at ? parseISO(row.created_at) : null;
                    const reference = [row.bank_name, row.transaction_reference_no, row.pay_order_no, row.notes]
                      .filter((v) => v && String(v).trim())
                      .join(" • ");

                    const originalIdx = sale.payments!.indexOf(row);
                    const receiptId = makeReceiptId(invoiceNo, originalIdx >= 0 ? originalIdx : idx);
                    const methodBadge = paymentMethodBadge(row.payment_method);

                    const txType = row.transaction_type_label ?? row.transaction_type ?? "Payment";
                    const isToken = (row.transaction_type ?? "").toLowerCase().includes("token") || txType.toLowerCase().includes("token");

                    return (
                      <tr key={row.id} className="hover:bg-muted/15 transition-colors">
                        {/* Date / Time */}
                        <td className="px-4 py-2 whitespace-nowrap sm:px-4">
                          <p className="text-xs font-bold text-foreground">
                            {date && isValid(date) ? format(date, "MMM d, yyyy") : (row.payment_date ?? "—")}
                          </p>
                          <p className="text-[10px] font-medium text-muted-foreground mt-0.5">
                            {createdAt && isValid(createdAt) ? format(createdAt, "hh:mm a") : ""}
                            {" "}PKT
                          </p>
                        </td>

                        {/* Type */}
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span className={cn(
                            "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold border",
                            isToken
                              ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800/60"
                              : "bg-slate-50 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700",
                          )}>
                            {isToken ? <ShieldCheck className="h-3 w-3 text-indigo-600 dark:text-indigo-400" /> : null}
                            {isToken ? "Token Deposit" : txType}
                          </span>
                        </td>

                        {/* Method & Channel */}
                        <td className="px-4 py-2">
                          <span className={cn(
                            "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold",
                            methodBadge.className,
                          )}>
                            {methodBadge.emoji} {methodBadge.label}
                          </span>
                          {row.bank_name ? (
                            <p className="mt-1 text-[10px] text-muted-foreground truncate max-w-[200px]">
                              Bank: {row.bank_name}
                            </p>
                          ) : null}
                        </td>

                        {/* Reference / Memo */}
                        <td className="px-4 py-2 max-w-[320px]">
                          <p className="text-xs text-foreground/90 font-medium" title={reference || undefined}>
                            {reference || "—"}
                          </p>
                        </td>

                        {/* Amount */}
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          <span className="text-sm font-extrabold text-foreground tabular-nums">
                            {formatCurrency(Number(row.amount))}
                          </span>
                        </td>

                        {/* Receipt */}
                        <td className="px-4 py-2 text-right whitespace-nowrap sm:px-4">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 gap-1.5 px-2.5 text-[11px] font-mono font-semibold text-blue-700 dark:text-blue-400 bg-blue-50/70 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900/60 hover:bg-blue-100 hover:text-blue-800 transition-colors shadow-2xs"
                            onClick={() => {
                              setSelectedReceiptPayment(row);
                              setSelectedReceiptId(receiptId);
                              setReceiptDialogOpen(true);
                            }}
                            title="View & Print Payment Receipt"
                          >
                            <Printer className="h-3 w-3" />
                            {receiptId}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredPayments.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-xs text-muted-foreground">
                        No payments match your filter.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

      </div>

      {/* ── CANCELLED BANNER ──────────────────────────────────────────── */}
      {isCancelled ? (
        <div
          className={cn(
            "mt-4 rounded-xl border px-4 py-3 text-sm",
            isReturned || isExchanged
              ? "border-violet-500/30 bg-violet-500/10 text-violet-900 dark:text-violet-200"
              : "border-rose-500/30 bg-rose-500/10 text-rose-800 dark:text-rose-200",
          )}
        >
          {isExchanged
            ? "This sale was exchanged. The original vehicle was restored to stock and a replacement sale was created."
            : isReturned
            ? "This sale was returned. Customer collections were refunded, sale books were reversed, and the vehicle was restored to stock."
            : "This sale is cancelled. Customer collections were refunded, sale books were reversed, and the vehicle was returned to stock."}
          {sale.cancel_notes ? <span className="mt-1 block text-xs opacity-90">{sale.cancel_notes}</span> : null}
        </div>
      ) : null}

      {/* ── DIALOGS ───────────────────────────────────────────────────── */}
      <AlertDialog open={cancelOpen} onOpenChange={(open) => !cancelSaving && setCancelOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this sale?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Customer walk-away will refund all money received on this sale, reverse sale accounting, and put the vehicle back in stock.
              </span>
              <span className="block text-foreground/80">
                Refund amount (customer paid):{" "}
                <strong>{formatCurrency(Number(sale.paid_total ?? 0))}</strong>
              </span>
              <span className="block text-xs text-muted-foreground">
                Owner/partner payouts already made are not auto-reversed.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelSaving}>Keep sale</AlertDialogCancel>
            <AlertDialogAction
              disabled={cancelSaving}
              className="bg-rose-600 text-white hover:bg-rose-700"
              onClick={(e) => {
                e.preventDefault();
                void submitCancelSale();
              }}
            >
              {cancelSaving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Cancel sale &amp; refund
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={extraProfitDetailsOpen} onOpenChange={setExtraProfitDetailsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Extra profit from cost recoveries</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">
            Amounts received above the scheduled cost line. This profit goes to the showroom / company.
          </p>
          {(summary?.extra_profit_details?.length ?? 0) > 0 ? (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-0.5">
              {(summary?.extra_profit_details ?? []).map((row) => (
                <div
                  key={row.id}
                  className="rounded-xl border border-border/50 bg-muted/15 px-3 py-2.5 space-y-1.5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{row.cost_label}</p>
                      {row.party_name ? (
                        <p className="text-[11px] text-muted-foreground truncate">From {row.party_name}</p>
                      ) : null}
                      {row.payment_date ? (
                        <p className="text-[10px] text-muted-foreground">{row.payment_date}</p>
                      ) : null}
                    </div>
                    <p className="shrink-0 text-sm font-extrabold text-amber-800 dark:text-amber-400 tabular-nums">
                      +{currSymbol} {formatAmount(Number(row.profit_amount))}
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-2 border-t border-border/40 pt-1.5 text-[10px]">
                    <div>
                      <span className="block text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                        Cost
                      </span>
                      <span className="font-bold text-foreground tabular-nums">
                        {currSymbol} {formatAmount(Number(row.actual_amount))}
                      </span>
                    </div>
                    <div>
                      <span className="block text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                        Received
                      </span>
                      <span className="font-bold text-foreground tabular-nums">
                        {currSymbol} {formatAmount(Number(row.received_amount))}
                      </span>
                    </div>
                    <div>
                      <span className="block text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                        Extra
                      </span>
                      <span className="font-bold text-amber-800 dark:text-amber-400 tabular-nums">
                        {currSymbol} {formatAmount(Number(row.profit_amount))}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="rounded-lg border border-dashed border-border/50 px-3 py-6 text-center text-xs text-muted-foreground">
              No cost-line recovery details found.
            </p>
          )}
          <div className="flex items-center justify-between border-t border-border/40 pt-3 text-sm">
            <span className="font-semibold text-foreground">Total extra profit</span>
            <span className="font-extrabold text-amber-800 dark:text-amber-400 tabular-nums">
              {currSymbol} {formatAmount(Number(summary?.extra_profit ?? 0))}
            </span>
          </div>
        </DialogContent>
      </Dialog>

      {sale.product_id || sale.product?.id ? (
        <ProductPaymentDialog
          open={investorPayOpen}
          onOpenChange={setInvestorPayOpen}
          productId={sale.product_id ?? sale.product!.id}
          outstanding={investorPayOutstanding}
          action="pay"
          partyName={investorPayPartyName}
          partyId={investorPayPartyId}
          transactionType="deal_partner"
          scheduledPaymentId={0}
          productInvoiceId={null}
          allowExcess={false}
          profitDistributionId={investorPayProfitDistributionId}
          acquisitionType={
            sale.product?.deal_type === "owner_vehicle" ? "consignment" : "purchased"
          }
          onSubmit={handleInvestorPayment}
        />
      ) : null}

      <GenerateGatePassDialog
        open={gatePassOpen}
        onOpenChange={setGatePassOpen}
        date={gatePassDate}
        time={gatePassTime}
        onDateChange={setGatePassDate}
        onTimeChange={setGatePassTime}
        saving={gatePassSaving}
        onSubmit={() => void submitGatePass()}
        checklistAttributes={checklistAttributes}
        checklistValues={checklistValues}
        onChecklistValueChange={(code, val) => {
          setChecklistValues((prev) => ({
            ...prev,
            [code]: val,
          }));
        }}
      />

      <RecordBalancePaymentDialog
        open={balanceDialogOpen}
        onOpenChange={setBalanceDialogOpen}
        outstanding={sale.outstanding ?? sale.balance_outstanding ?? 0}
        onSubmit={async (payload) => {
          try {
            const r = await paySaleBalance(sale.id, payload);
            toast.success(r.message);
            if (r.record) {
              setSale(r.record);
            }
            await reload();
          } catch (e) {
            toast.error(saleInvoiceErrorMessage(e));
            throw e;
          }
        }}
      />

      <RecordBalancePaymentDialog
        open={downPaymentDialogOpen}
        onOpenChange={setDownPaymentDialogOpen}
        outstanding={sale.down_payment_outstanding ?? 0}
        title="Record Down Payment"
        description="Record the remaining down payment for this installment sale."
        balanceLabel="Remaining Down Payment"
        defaultNoteText="Installment Down Payment"
        onSubmit={async (payload) => {
          try {
            const r = await paySaleBalance(sale.id, payload);
            toast.success(r.message);
            if (r.record) {
              setSale(r.record);
            }
            await reload();
          } catch (e) {
            toast.error(saleInvoiceErrorMessage(e));
            throw e;
          }
        }}
      />

      <RecordBalancePaymentDialog
        open={brokerPaymentDialogOpen}
        onOpenChange={setBrokerPaymentDialogOpen}
        outstanding={sale.broker_commission_outstanding ?? 0}
        title="Record Broker Payment"
        description={
          sale.broker?.name
            ? `Pay outstanding broker commission to ${sale.broker.name}.`
            : "Pay outstanding broker commission."
        }
        balanceLabel="Broker commission due"
        defaultNoteText="Broker commission payment"
        onSubmit={async (payload) => {
          try {
            const r = await payBrokerCommission(sale.id, payload);
            toast.success(r.message);
            if (r.record) {
              setSale(r.record);
            }
            await reload();
          } catch (e) {
            toast.error(saleInvoiceErrorMessage(e));
            throw e;
          }
        }}
      />

      <RecordInstallmentPaymentDialog
        open={paymentTarget != null}
        onOpenChange={(open) => !open && setPaymentTarget(null)}
        installment={paymentTarget}
        saleOutstanding={installmentOutstanding}
        onSubmit={async (payload) => {
          if (!paymentTarget) return;
          try {
            const r = await paySaleInstallment(sale.id, paymentTarget.id, payload);
            toast.success(r.message);
            if (r.record) {
              setSale(r.record);
            }
            await reload();
            setPaymentTarget(null);
          } catch (e) {
            toast.error(saleInvoiceErrorMessage(e));
            throw e;
          }
        }}
      />

      {sale && (
        <PaymentReceiptDialog
          open={receiptDialogOpen}
          onOpenChange={setReceiptDialogOpen}
          sale={sale}
          payment={selectedReceiptPayment}
          receiptId={selectedReceiptId}
        />
      )}

      {sale && (sale.product_id || sale.product?.id) && (
        <ProductLedgerDialog
          open={ledgerModalOpen}
          productId={sale.product_id ?? sale.product!.id}
          onOpenChange={setLedgerModalOpen}
        />
      )}
    </Layout>
  );
};

export default SaleInvoice;
